// src/components/common/GcsImage.tsx
/* eslint-disable @next/next/no-img-element */

import type { ImgHTMLAttributes } from "react";
// Legacy: The GCS URL constant is required only for direct GCS access.
// When routing via the Load Balancer, relative paths are used instead.
// import { GCS_ASSETS_URL } from "@/lib/constants";
import { imageDimensions } from "@/lib/imageDimensions";

// Strongly type valid paths to the keys generated in imageDimensions
export type GcsPath = keyof typeof imageDimensions;

type Props = Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "width" | "height"> & {
  /** Path inside the bucket, e.g. "images/example.webp" */
  path: GcsPath;

  /** Optional rendered width/height. If omitted, defaults to original dimensions. */
  width?: number;
  height?: number;

  /** Kept for API compatibility with next/image, ignored by <img> directly. */
  fill?: boolean;
  priority?: boolean;
};

export default function GcsImage({
  path,
  width,
  height,
  fill,       // Pulled out to prevent hitting the DOM
  priority,   // Pulled out to prevent hitting the DOM
  sizes,      // Pulled out - not used with plain <img>, only with srcset
  fetchPriority,
  loading,
  alt = "",
  ...rest
}: Props) {
  // ---------------------------------------------------------------------------
  // IMAGE SOURCE RESOLUTION
  // ---------------------------------------------------------------------------
  
  // OPTION A: Direct GCS Access (Legacy)
  // Bypasses the Load Balancer and Cloud Armor firewall; hits the public bucket directly.
  // Requires GCS_ASSETS_URL to be defined (e.g., "https://storage.googleapis.com/...").
  // const src = `${GCS_ASSETS_URL}/${path}`;

  // OPTION B: Load Balancer (LB) Access (Active)
  // Uses a root-relative path (e.g., "/images/hero.webp").
  // This directs the browser to send the request to the current domain (the Load Balancer).
  // The LB's URL Map routes specific path prefixes (e.g., /images/*) to the Backend Bucket,
  // ensuring traffic is inspected by Cloud Armor and cached by Cloud CDN.
  const src = `/${path}`;

  const dims = imageDimensions[path];

  /**
   * ARCHITECTURE NOTE:
   * This component deliberately renders a plain <img>, *not* next/image.
   *
   * - No Next.js image optimization is performed.
   * - No /_next/image proxying occurs.
   * - The browser loads the raw file directly via the Load Balancer/CDN.
   *
   * The `priority` prop is mapped to `loading="eager"` instead of
   * being written to the DOM as a boolean attribute.
   */

  // Derive missing dimension based on natural aspect ratio (from imageDimensions)
  let w = width;
  let h = height;

  if ((w == null || h == null) && dims) {
    const ratio = dims.height / dims.width;

    if (w != null && h == null) {
      h = Math.round(w * ratio);
    } else if (h != null && w == null) {
      w = Math.round(h / ratio);
    } else if (w == null && h == null) {
      w = dims.width;
      h = dims.height;
    }
  }

  if (w == null || h == null) {
    // Note: This error would be thrown on the SERVER during build/render
    throw new Error(
      `GcsImage: dimensions missing for "${path}". Add it to imageDimensions or pass width/height.`
    );
  }

  // If priority is true, force eager loading + high fetch priority.
  // Otherwise default to lazy loading and leave fetch priority unset unless provided.
  const effectiveLoading: ImgHTMLAttributes<HTMLImageElement>["loading"] =
    loading ?? (priority ? "eager" : "lazy");
  const effectiveFetchPriority: ImgHTMLAttributes<HTMLImageElement>["fetchPriority"] =
    priority ? "high" : fetchPriority;

  // Keep compatibility props out of the DOM while marking them as intentionally consumed.
  void fill;
  void sizes;

  return (
    <img
      src={src}
      alt={alt}
      width={w}
      height={h}
      loading={effectiveLoading}
      fetchPriority={effectiveFetchPriority}
      {...rest}   // `fill`, `priority`, and `sizes` have been stripped to prevent DOM warnings
    />
  );
}
